import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { List, Columns3, CalendarDays, AlertTriangle, Truck, Store, Wallet, PackageCheck, Briefcase, Building2, CircleDot, ListTodo,
  CalendarClock, Clock, Banknote, CaseSensitive } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { storage } from "@/lib/storage";
import { formatCZK, formatCZKCompact, plur } from "@/lib/format";
import { StatCard, useIsMobile } from "@/components/shared/PagePrimitives";
import { ListHeader, ListToolbar } from "@/components/shared/ListToolbar";
import { useListFilters } from "@/hooks/useListFilters";
import { useGroupSort } from "@/components/shared/GroupSort";
import { AmountPick, AM_HINT } from "@/components/shared/AmountPick";
import { AMOUNT_MODES, defaultAmountMode } from "@/lib/documents";
import { EDIT_ROLES, RECEIVE_ROLES, todayISO, addDays, mondayOf, projectLabel, statusForColumn, STATUS, BUCKETS, MODES, pillOf } from "@/lib/orders";
import { OrderList } from "@/components/orders/OrderList";
import { OrderBoard } from "@/components/orders/OrderBoard";
import { OrderCalendar } from "@/components/orders/OrderCalendar";
import { OrderDetail } from "@/components/orders/OrderDetail";
import { OrderDialog } from "@/components/orders/OrderDialog";
import { ReceiveSheet } from "@/components/orders/ReceiveSheet";
import { DeliveriesMobile } from "@/components/orders/DeliveriesMobile";

const VIEWS = [
  { value: "list", label: "Seznam", icon: List, testid: "orders-view-list" },
  { value: "board", label: "Tabule", icon: Columns3, testid: "orders-view-board" },
  { value: "calendar", label: "Kalendář", icon: CalendarDays, testid: "orders-view-calendar" },
];
const NONE = [];
const fetchOrders = (params) => () => api.get("/orders", { params }).then((r) => r.data);
const hay = (o) => [o.supplier_name, o.title, o.order_no, o.project_name, ...(o.lines || []).map((l) => l.name)].join(" ").toLowerCase();

// 391: jednotná lišta jako Doklady — Seskupit (výchozí Co dělat = dřívější skupiny) a Řadit (výchozí Termín),
// Filtr Zakázka / Dodavatel / Stav / Doručení (?zakazka= zůstává), Částky bez DPH / s DPH / obojí.
const BUCKET_ORDER = [...BUCKETS.map((b) => b.key), "done", "cancelled"];
const BUCKET_NAME = { ...Object.fromEntries(BUCKETS.map((b) => [b.key, b.title])), done: "Vyřízeno", cancelled: "Zrušeno" };
const STAV = ["Nedorazilo", "Objednáno", "Potvrzeno", "Na cestě", "K vyzvednutí", "Převzato", "Vyřízeno", "Zrušeno"];
const GROUPS = {
  bucket: { label: "Co dělat", icon: ListTodo, key: (o) => o.bucket || "", name: (k) => BUCKET_NAME[k] || k,
    order: (a, b) => BUCKET_ORDER.indexOf(a) - BUCKET_ORDER.indexOf(b), dirs: ["Řešit teď první", "Převzato první"] },
  project: { label: "Zakázka", icon: Briefcase, key: (o) => projectLabel(o) },
  supplier: { label: "Dodavatel", icon: Building2, key: (o) => o.supplier_name || "" },
  status: { label: "Stav", icon: CircleDot, key: (o) => pillOf(o).label, order: (a, b) => STAV.indexOf(a) - STAV.indexOf(b), dirs: ["Nedorazilo první", "Zrušeno první"] },
  delivery: { label: "Doručení", icon: Truck, key: (o) => MODES[o.delivery?.mode || "site"].label },
};
const ORDERS = {
  due: { label: "Termín", icon: CalendarClock, dirs: ["Nejdřív", "Nejpozději"], def: "asc", val: (o) => o.due_date || "" },
  created: { label: "Datum objednání", icon: Clock, dirs: ["Nejstarší", "Nejnovější"], def: "desc", val: (o) => o.created_at || "" },
  received: { label: "Datum převzetí", icon: PackageCheck, dirs: ["Nejstarší", "Nejnovější"], def: "desc", val: (o) => o.received?.at || "" },
  amount: { label: "Částka", icon: Banknote, dirs: ["Od nejnižší", "Od nejvyšší"], def: "desc", val: (o) => o.amount_gross ?? "" },
  supplier: { label: "Dodavatel", icon: CaseSensitive, dirs: ["A → Z", "Z → A"], def: "asc", val: (o) => o.supplier_name || "" },
};
const { amount: _a, ...ORDERS_NO_MONEY } = ORDERS;   // bez práv k penězům se podle částky neřadí
const DEF_VIEW = { g: "bucket", gd: 0, k: "due", dir: "asc" };
const byCreated = (a, b) => (b.created_at || "").localeCompare(a.created_at || "");

// Objednávky (311): PC = Seznam / Tabule / Kalendář (přepínač ikonami), telefon = Dodávky.
// Kancelář objednává, stavba přebírá s fotkou; „vyřízeno“ = převzato i zaplaceno (server).
export default function Orders() {
  const { user, company } = useAuth();
  const mobile = useIsMobile();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const canEdit = EDIT_ROLES.includes(user?.role);
  const canReceive = RECEIVE_ROLES.includes(user?.role);
  const money = canEdit;

  const [status, setStatus] = useState("open");
  const [view, setViewState] = useState(() => storage.get("pp-orders-view") || "list");
  const setView = (v) => { setViewState(v); storage.set("pp-orders-view", v); };
  const [search, setSearch] = useState("");
  const qk = useDeferredValue(search).trim().toLowerCase();
  const [monday, setMonday] = useState(() => mondayOf(todayISO()));
  // 391: Zobrazení › Částky — výchozí podle firmy (plátce bez DPH), volba se pamatuje na zařízení
  const defAm = defaultAmountMode(company, user);   // 394: osobní volba z Nastavení
  const [amPref, setAmPref] = useState(() => { const a = storage.get("orders_amounts"); return AMOUNT_MODES[a] ? a : null; });
  const am = amPref || defAm;
  const setAm = (a) => { const v = a === defAm ? null : a; setAmPref(v); if (v) storage.set("orders_amounts", v); else storage.remove("orders_amounts"); };
  const [dialog, setDialog] = useState(null);   // { order } | {} = nová
  const [receive, setReceive] = useState(null);
  const oid = params.get("o");

  const calendar = !mobile && view === "calendar";
  const openQ = useQuery({ queryKey: ["orders", "open"], queryFn: fetchOrders({ status: "open" }), staleTime: 20_000 });
  const doneQ = useQuery({ queryKey: ["orders", "done"], queryFn: fetchOrders({ status: "done" }), enabled: mobile || (status === "done" && !calendar), staleTime: 20_000 });
  const cancQ = useQuery({ queryKey: ["orders", "cancelled"], queryFn: fetchOrders({ status: "cancelled" }), enabled: !mobile && status === "cancelled" && !calendar, staleTime: 20_000 });
  const otherQ = status === "done" ? doneQ : cancQ;
  const calQ = useQuery({ queryKey: ["orders", "all", monday], queryFn: fetchOrders({ status: "all", date_from: monday, date_to: addDays(monday, 6) }),
    enabled: calendar, placeholderData: keepPreviousData, staleTime: 20_000 });

  const openAll = openQ.data?.items || NONE;
  const otherAll = (status === "open" ? openAll : otherQ.data?.items) || NONE;

  // Volby filtrů z načtených objednávek — jen zakázky a dodavatelé, kde něco je.
  const known = useMemo(() => [...openAll, ...(doneQ.data?.items || []), ...(cancQ.data?.items || []), ...(calQ.data?.items || [])],
    [openAll, doneQ.data, cancQ.data, calQ.data]);
  const projOpts = useMemo(() => {
    const m = new Map();
    known.forEach((o) => m.set(o.project_id || "none", projectLabel(o)));
    return [...m.entries()].map(([value, label]) => ({ value, label }))
      .sort((a, b) => (a.value === "none") - (b.value === "none") || a.label.localeCompare(b.label, "cs"));
  }, [known]);
  const supOpts = useMemo(() => {
    const m = new Map();
    known.forEach((o) => m.set(o.supplier_id, o.supplier_name));
    return [...m.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, "cs"));
  }, [known]);
  const stavOpts = useMemo(() => { const has = new Set(known.map((o) => pillOf(o).label)); return STAV.filter((l) => has.has(l)).map((l) => ({ value: l, label: l })); }, [known]);

  // Filtr (391): stejná pole pro seznam, tabuli, kalendář i KPI; hledání přes dodavatele, číslo, položky
  const fgroups = useMemo(() => [
    { key: "zakazka", label: "Zakázka", icon: Briefcase, options: projOpts, match: (o, v) => (v === "none" ? !o.project_id : o.project_id === v) },
    { key: "dodavatel", label: "Dodavatel", icon: Building2, options: supOpts, match: (o, v) => o.supplier_id === v },
    { key: "stav", label: "Stav", icon: CircleDot, options: stavOpts, match: (o, v) => pillOf(o).label === v },
    { key: "doruceni", label: "Doručení", icon: Truck, options: Object.entries(MODES).map(([value, m]) => ({ value, label: m.label })),
      match: (o, v) => (o.delivery?.mode || "site") === v },
  ], [projOpts, supOpts, stavOpts]);
  const base = calendar ? (calQ.data?.items || NONE) : otherAll;
  const F = useListFilters(base, { groups: fgroups, entity: { one: "objednávka", few: "objednávky", many: "objednávek" }, search: hay, searchTerm: qk });
  const passF = (o) => (!qk || hay(o).includes(qk)) && fgroups.every((g) => {
    const f = F.state[g.key];
    if (!f) return true;
    const has = f.vals.some((v) => g.match(o, v));
    return f.neg ? !has : has;
  });
  const openItems = useMemo(() => openAll.filter(passF), [openAll, F.state, qk]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = F.visible || NONE;
  const calItems = calendar ? items : NONE;
  const project = F.state.zakazka?.vals?.length === 1 && !F.state.zakazka.neg ? F.state.zakazka.vals[0] : "";
  const GS = useGroupSort(calendar ? NONE : items, { storageKey: "orders_view", groups: GROUPS, orders: money ? ORDERS : ORDERS_NO_MONEY, def: DEF_VIEW, tie: byCreated,
    extra: money ? { render: (m) => <AmountPick am={am} defAm={defAm} mobile={m} onPick={setAm} testid="orders-amounts" />,
      active: !!amPref, hint: AM_HINT[am], def: AM_HINT[defAm], reset: () => setAm(defAm) } : null });

  const detailQ = useQuery({ queryKey: ["order", oid], queryFn: () => api.get(`/orders/${oid}`).then((r) => r.data), enabled: !!oid,
    placeholderData: () => known.find((o) => o.id === oid) });
  useEffect(() => {
    if (oid && detailQ.isError) { toast.error(apiError(detailQ.error)); closeDetail(); }
  }, [oid, detailQ.isError]); // eslint-disable-line react-hooks/exhaustive-deps
  const openDetail = (o) => setParams((p) => { const n = new URLSearchParams(p); n.set("o", o.id); return n; });
  const closeDetail = () => setParams((p) => { const n = new URLSearchParams(p); n.delete("o"); return n; }, { replace: true });

  const changed = (o) => {
    if (o?.id) qc.setQueryData(["order", o.id], o);
    qc.invalidateQueries({ queryKey: ["orders"] });
  };
  const move = async (o, col) => {
    const st = statusForColumn(col, o);
    try { changed((await api.patch(`/orders/${o.id}`, { status: st })).data); toast.success(`${o.supplier_name}: ${STATUS[st].label.toLowerCase()}`); }
    catch (err) { toast.error(apiError(err)); }
  };

  // KPI z rozpracovaných (po filtrech): co hoří, co přijde, co vyzvednout, kolik je v objednávkách.
  const k = useMemo(() => {
    const today = todayISO(), tomorrow = addDays(today, 1);
    const by = (b) => openItems.filter((o) => o.bucket === b);
    const now = by("now"), week = by("week"), pick = by("pick"), got = by("got");
    const sum = (key) => openItems.reduce((a, o) => a + (o[key] || 0), 0);
    return {
      now, week, pick, got, gross: sum("amount_gross"), net: sum("amount_net"),
      late: now.filter((o) => o.late).length, noTerm: now.filter((o) => o.no_term).length, problem: now.filter((o) => o.problem).length,
      today: week.filter((o) => o.due_date === today).length, tomorrow: week.filter((o) => o.due_date === tomorrow).length,
      branches: [...new Set(pick.map((o) => o.delivery?.branch).filter(Boolean))],
    };
  }, [openItems]);

  const loading = openQ.isLoading || (status !== "open" && !calendar && otherQ.isLoading);
  const dlgProps = { onClose: () => setDialog(null), onSaved: (o) => { changed(o); if (!dialog?.order) openDetail(o); } };

  const shared = (
    <>
      <OrderDetail order={detailQ.data} open={!!oid && !!detailQ.data} onOpenChange={(o) => { if (!o) closeDetail(); }}
        canEdit={canEdit} canReceive={canReceive} onChanged={changed} onEdit={(o) => setDialog({ order: o })} onReceive={setReceive} />
      <OrderDialog open={!!dialog} order={dialog?.order} defaultProjectId={project && project !== "none" ? project : ""} {...dlgProps} />
      <ReceiveSheet order={receive} onClose={() => setReceive(null)} onDone={changed} />
    </>
  );

  if (mobile) {
    const doneItems = (doneQ.data?.items || NONE).filter(passF);
    return (
      <>
        <DeliveriesMobile open={openItems} done={doneQ.data ? doneItems : null} tab={status === "done" ? "done" : "open"}
          onTab={(t) => setStatus(t)} projects={projOpts} project={project} onProject={(v) => F.setRaw("zakazka", v)}
          canEdit={canEdit} canReceive={canReceive} money={money} onOpen={openDetail} onReceive={setReceive} onNew={() => setDialog({})} loading={loading} />
        {shared}
      </>
    );
  }

  return (
    <div className="space-y-5" data-testid="orders-page">
      <ListHeader title="Objednávky" mobile={false} testid="orders-header"
        summary={`${plur(openAll.length, "rozpracovaná", "rozpracované", "rozpracovaných")} · kancelář objednává, stavba přebírá s fotkou`}
        search={search} onSearch={setSearch} searchPh="Dodavatel, číslo, položka…"
        primary={canEdit ? { label: "Objednávka", full: "Nová objednávka", onClick: () => setDialog({}), testid: "order-new" } : undefined} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="orders-kpis">
        <StatCard icon={AlertTriangle} label="Řešit teď" value={k.now.length} danger testid="kpi-now"
          sub={[k.late && `${k.late} nedorazilo`, k.noTerm && `${k.noTerm} bez termínu`, k.problem && `${k.problem} problém`].filter(Boolean).join(" · ") || "nic nehoří"}
          onClick={() => { setStatus("open"); setView("list"); }} />
        <StatCard icon={Truck} label="Přijde tento týden" value={k.week.length} testid="kpi-week"
          sub={[`${k.today} dnes`, `${k.tomorrow} zítra`].join(" · ")} />
        <StatCard icon={Store} label="K vyzvednutí" value={k.pick.length} testid="kpi-pick" sub={k.branches.join(", ") || "nic nečeká"} />
        {money ? (
          <StatCard icon={Wallet} label="Objednáno, nevyřízeno" value={formatCZKCompact(am === "gross" ? k.gross : k.net)} title={`${formatCZK(k.gross)} s DPH · ${formatCZK(k.net)} bez DPH`} testid="kpi-money"
            sub={am === "gross" ? `s DPH · ${formatCZKCompact(k.net)} bez DPH` : `bez DPH · ${formatCZKCompact(k.gross)} s DPH`} />
        ) : (
          <StatCard icon={PackageCheck} label="Převzato" value={k.got.length} sub="čeká na platbu" testid="kpi-got" />
        )}
      </div>

      <ListToolbar mobile={false} testid="orders-toolbar"
        tabs={calendar ? undefined : [
          { value: "open", label: "Rozpracované", count: openItems.length },
          { value: "done", label: "Vyřízené" },
          { value: "cancelled", label: "Zrušené" },
        ]} tab={status} onTab={setStatus}
        filter={F} search={search} onSearch={setSearch} searchPh="Dodavatel, číslo, položka…"
        sort={calendar || view === "board" ? (money ? { ...GS.sortProp, render: ({ mobile: m }) => (
          <div className="p-3.5 space-y-2" data-testid="orders-view-amounts"><AmountPick am={am} defAm={defAm} mobile={m} onPick={setAm} testid="orders-amounts" /></div>) } : undefined) : GS.sortProp}
        views={VIEWS} view={view} onView={setView} />

      {loading ? <div className="text-muted-foreground" data-testid="orders-loading">Načítám…</div>
        : view === "board" && status === "open" ? <OrderBoard items={items} money={money} am={am} canEdit={canEdit} onOpen={openDetail} onMove={move} onReceive={setReceive} />
        : calendar ? <OrderCalendar items={calItems} open={openItems} monday={monday} onWeek={setMonday} money={money} am={am} onOpen={openDetail} />
        : <OrderList rows={GS.sorted} groups={GS.groups} closed={GS.closed} onToggle={GS.toggle} groupBy={GS.view.g} status={status} money={money} am={am} onOpen={openDetail} />}
      {view === "board" && status !== "open" && <p className="text-[13px] text-muted-foreground">Tabule ukazuje jen rozpracované — vyřízené a zrušené jsou jako seznam.</p>}

      {shared}
    </div>
  );
}
